/**
 * How every menu button looks when it is focused and when it is pressed.
 *
 * Before this, each menu chose its own: Results grew its focused item by 6%,
 * the profile cards and pause menu by 8%, Settings not at all, and nothing
 * anywhere showed a press. Mouse and keyboard now go through the same two
 * calls, so Enter and a click look identical in every menu.
 */
import type Phaser from 'phaser';
import { PALETTE } from '../../types';

export const BUTTON_FILL = '#1b2a41';
export const BUTTON_FILL_FOCUS = '#2b4166';
export const FOCUS_SCALE = 1.06;
export const PRESS_SCALE = 0.93;
/** Long enough to see the press, short enough that a menu never feels slow. */
export const PRESS_MS = 90;

/**
 * Show one menu item as focused or not.
 *
 * Items drawn as buttons (with a background) also change fill; plain list rows
 * only change colour and size. Safe to call on a destroyed text, which Phaser
 * would otherwise throw on.
 */
export function styleButton(text: Phaser.GameObjects.Text, focused: boolean): void {
  if (!text.active) return;
  text.setColor(focused ? PALETTE.gold : PALETTE.snow);
  if (text.style.backgroundColor) {
    text.setBackgroundColor(focused ? BUTTON_FILL_FOCUS : BUTTON_FILL);
  }
  if (text.getData('pressing') !== true) text.setScale(focused ? FOCUS_SCALE : 1);
}

/**
 * Dip the button, then run its action.
 *
 * Deliberately independent of the Phaser clock. The pause menu lives in a
 * scene whose tweens and timers are paused while it is open, so a press built
 * on scene.tweens or scene.time would never complete and the button would do
 * nothing. A plain timeout and a direct scale change work whatever the scene's
 * clock is doing, and are not motion in the reduced-motion sense: the button
 * changes state, it does not travel.
 *
 * A press fires once. A double click, or Enter held down and repeating, during
 * the dip would otherwise start the next scene twice.
 */
export function pressButton(text: Phaser.GameObjects.Text, action: () => void): void {
  if (!text.active || text.getData('pressing') === true) return;
  text.setData('pressing', true);

  const settled = text.scaleX;
  text.setScale(settled * PRESS_SCALE);

  window.setTimeout(() => {
    // The scene may have changed while the button was down.
    if (!text.active) return;
    text.setScale(settled);
    text.setData('pressing', false);
    action();
  }, PRESS_MS);
}
